import React, { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import cx from 'classnames';
import { CDN_TYPE_SPORT, getCDNUrl } from 'utils';

import {
  constants,
  DateService,
  SiteConfigManager,
  useApplicationState,
  useArchivedSportsbookState,
  usePreferences,
} from '@atlas/core';
import { EventDetailsBreadcrumbs, EventParticipants, FavoriteButton, Icon, Label } from '@atlas/ui';

import { sportService as sportServices } from 'common/constants';
import BetBooster from 'components/Pages/Details/BetBooster/BetBooster';
import { StatisticButton } from 'components/Pages/Details/LiveCenter/StatisticButton';
import { defaultRoutes } from 'src/components/MainRouter/constants';

import GameStats from './GameStats/GameStats';
import PropTypes from "prop-types";

const { ARCHIVED, LIVE } = sportServices;

const DetailsEventHeader = ({ disclaimer = null, event, isResults = false, suspended }) => {
  const {
    categoryId,
    categoryImagePaths,
    categoryName,
    id,
    name,
    outright,
    participants,
    regionId,
    sportId,
    sportName,
    sportService,
    startTime,
    tournamentId,
    tournamentImagePaths,
    tournamentName,
    widgets,
  } = event;
  const imagePaths = {
    categoryImagePaths,
    tournamentImagePaths,
  };

  const shownEventId = SiteConfigManager.getConfig('config.config.sportsbook.showEventId');
  const { getIsEventFavorite, selectFavoriteEvent } = usePreferences();
  const isFavorite = getIsEventFavorite({ eventId: id });
  const applicationState = useApplicationState();
  const { authenticated } = applicationState;

  const isShowParticipants = useMemo(() => {
    if (!participants) return false;
    return Object.keys(participants).length <= 2 && Object.keys(participants).length !== 0;
  }, [participants]);
  const isStatisticAvailable = React.useMemo(
    () => widgets?.some(widget => widget.type === 'STATISTIC'),
    [widgets],
  );

  const { push: historyPush } = useHistory();
  const { pathname } = useLocation();
  const { updateArchivedSportsbookState } = useArchivedSportsbookState();

  const selectFavoriteEventHandler = useCallback(() => {
    selectFavoriteEvent({ eventId: id });
  }, [selectFavoriteEvent, id]);

  const detailsHeaderClass = cx('details-event-prematch-event-info', {
    'details-live': sportService === constants.SPORT_SERVICE.LIVE,
  });

  const handleBackButtonClick = useCallback(
    e => {
      e.preventDefault();

      if (isResults) {
        updateArchivedSportsbookState({
          selectedEventId: null,
          selectedEventUrlName: null,
        });
        return historyPush(pathname);
      }

      return historyPush(defaultRoutes.sportPrematch);
    },
    [isResults, updateArchivedSportsbookState, historyPush, pathname],
  );

  const statisticData = useMemo(() => {
    if (isStatisticAvailable) {
      return {
        sportId,
        widgets,
      };
    }
    return {};
  }, [isStatisticAvailable, sportId, widgets]);

  const headerPrematch = useMemo(
    () => (
      <>
        <div className={detailsHeaderClass}>
          <div className="icon-box">
            <div className="icon-container">
              <span onClick={handleBackButtonClick}>
                <Icon className="icon-back" name="back" />
              </span>
            </div>
          </div>
          {sportService === constants.SPORT_SERVICE.LIVE ? (
            <GameStats event={event} />
          ) : (
            <div className="team-time-info">
              {isShowParticipants ? (
                <EventParticipants participants={participants} showSportLogo={false} />
              ) : (
                <span className="event-name">{event.name}</span>
              )}
              <div>
                {startTime && (
                  <span className="date-info">
                    <span className="title-small-text">
                      {DateService.getFormattedDate(startTime)}
                    </span>{' '}
                    <span>{DateService.getFormattedTime(startTime)}</span>
                  </span>
                )}
                {shownEventId && (
                  <span className="sub-title-xsmall-500-text">
                    ID:
                    {id}
                  </span>
                )}
              </div>
            </div>
          )}
          {authenticated && sportService !== ARCHIVED && (
            <FavoriteButton
              active={isFavorite}
              className="icon-box box-width"
              onClick={selectFavoriteEventHandler}
            />
          )}
          {sportService !== LIVE && isStatisticAvailable && (
            <StatisticButton type="icon" widgetData={statisticData} />
          )}
        </div>
        {disclaimer && (
          <div className="details-event-disclaimer-wrapper">
            <Label className="details-event-disclaimer" message="live_statistics_disclaimer" />
          </div>
        )}
      </>
    ),
    [
      detailsHeaderClass,
      handleBackButtonClick,
      sportService,
      event,
      isShowParticipants,
      participants,
      startTime,
      shownEventId,
      id,
      authenticated,
      isFavorite,
      selectFavoriteEventHandler,
      isStatisticAvailable,
      statisticData,
    ],
  );

  const detailHeaderClassName = cx('details-event-header-container', {
    active: authenticated && selectFavoriteEvent,
  });

  function onErrorTournamentFlagHandler({ target = {} }) {
    const flagClassName = target.parentNode.className;
    target.parentNode.className = `${flagClassName} d-none`;
  }

  return (
    <>
      <div className={detailHeaderClassName}>
        <h1 className="header-title__name d-none">
          {sportName} {categoryName} {tournamentName}
          {outright
            ? `${name}`
            : `${participants?.home?.fullName} -
        ${participants?.away?.fullName}`}
        </h1>
        <EventDetailsBreadcrumbs
          categoryId={tournamentId}
          categoryName={categoryName}
          cdnHostname={getCDNUrl(CDN_TYPE_SPORT)}
          imagePaths={imagePaths}
          onErrorTournamentFlagHandler={onErrorTournamentFlagHandler}
          regionId={isResults ? categoryId : regionId}
          sportId={sportId}
          sportName={sportName}
          tournamentName={tournamentName}
        />
      </div>
      {headerPrematch}
      <BetBooster event={event} suspended={suspended} />
    </>
  );
};

DetailsEventHeader.propTypes = {
  disclaimer: PropTypes.bool,
  event: PropTypes.object,
  isResults: PropTypes.bool,
  suspended: PropTypes.bool,
};

export default DetailsEventHeader;
